import React, { useEffect, useState } from 'react';
import { OptionalCookies } from '../../hooks/useCookieConsent';
import { ButtonPopup } from '../ui';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import { motion } from 'framer-motion';


interface Props {
  onAccept?: (optionalCookies: OptionalCookies) => void;
  onReject?: () => void;
  onClose?: () => void;
}

const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const setCookie = (name: string, value: string, maxAgeSeconds = 31536000) => {
  document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}`;
};

const CookieConsentModal: React.FC<Props> = ({ onAccept, onReject, onClose }) => {
  const [analyticsCookies, setAnalyticsCookies] = useState(false);
  const [personalizationCookies, setPersonalizationCookies] = useState(false);
  const [adsCookies, setAdsCookies] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const analytics = getCookie('analytics') === 'true';
    const personalization = getCookie('personalization') === 'true';
    const ads = getCookie('ads') === 'true';

    setAnalyticsCookies(analytics);
    setPersonalizationCookies(personalization);
    setAdsCookies(ads);
  }, []);

  const handleAccept = () => {
    setCookie('analytics', analyticsCookies.toString());
    setCookie('personalization', personalizationCookies.toString());
    setCookie('ads', adsCookies.toString());

    onAccept?.({
      analytics: analyticsCookies,
      personalization: personalizationCookies,
      ads: adsCookies,
    });

    closeModal();

    setTimeout(() => {
      navigate('/');
    }, 100);
  };

  const handleReject = () => {
    onReject?.();
    closeModal();

    setTimeout(() => {
      navigate('/paginaCookieRechazada');
    }, 100);
  };

  const closeModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose?.();
    }, 200);
  };

 return (
  <Modal onClose={closeModal} closeOnOverlay={false}>
    <motion.div
      initial={{ opacity: 2, y: 50, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 1 }}
      className="relative z-10 max-w-md w-full"
    >
      <div
        className={`p-8 rounded-2xl border-2 border-blue-500 shadow-md shadow-blue-700 bg-transparent backdrop-blur-5xl text-white transition-all duration-300 ${
          isClosing ? "scale-95 opacity-0" : "scale-100 opacity-100"
        }`}
      >
        <h2 className="text-2xl font-bold mb-6 text-center">
          Configuración de Cookies
        </h2>

        {/* Cookies necesarias */}
        <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
          <div>
            <p className="font-semibold">Cookies necesarias</p>
            <p className="text-sm text-white/70">
              Esenciales para el funcionamiento del sitio.
            </p>
          </div>

          <div className="relative inline-flex items-center cursor-not-allowed">
            <div className="w-11 h-6 bg-blue-600 rounded-full"></div>
            <div className="absolute left-5 w-5 h-5 bg-white rounded-full"></div>
          </div>
        </div>

        {/* Cookies opcionales */}
        {[{
          label: 'Cookies de análisis',
          description: 'Ayudan a entender cómo usas el sitio.',
          state: analyticsCookies,
          setState: setAnalyticsCookies
        }, {
          label: 'Cookies de personalización',
          description: 'Recuerdan tus preferencias.',
          state: personalizationCookies,
          setState: setPersonalizationCookies
        }, {
          label: 'Cookies de publicidad',
          description: 'Se usan para mostrar anuncios relevantes.',
          state: adsCookies,
          setState: setAdsCookies
        }].map(({ label, description, state, setState }, index) => (
          <div className="flex justify-between items-center mb-4" key={index}>
            <div>
              <p className="font-semibold">{label}</p>
              <p className="text-sm text-white/70">{description}</p>
            </div>

            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={state}
                onChange={() => setState(!state)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-white/20 peer-checked:bg-blue-600 rounded-full transition-colors"></div>
              <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
            </label>
          </div>
        ))}

        {/* Botones */}
        <div className="flex justify-center gap-4 mt-8">
          <ButtonPopup
            onClick={handleReject}
            className="min-w-[10rem] bg-red-600 hover:bg-red-700 text-white rounded-lg shadow-md transition"
          >
            Rechazar
          </ButtonPopup>

          <ButtonPopup
            onClick={handleAccept}
            className="min-w-[10rem] bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-md transition"
          >
            Aceptar
          </ButtonPopup>
        </div>
      </div>
    </motion.div>
  </Modal>
);
};

export default CookieConsentModal;